export class PdfOperationCancelledError extends Error {
  readonly name = "PdfOperationCancelledError";

  constructor(message = "The PDF operation was cancelled.") {
    super(message);
  }
}

export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new PdfOperationCancelledError();
  }
}

export function isPdfCancellationError(error: unknown): boolean {
  if (error instanceof PdfOperationCancelledError) {
    return true;
  }

  if (
    typeof DOMException !== "undefined" &&
    error instanceof DOMException &&
    error.name === "AbortError"
  ) {
    return true;
  }

  return (
    error instanceof Error &&
    (error.name === "RenderingCancelledException" ||
      error.name === "AbortException")
  );
}

export function onAbort(
  signal: AbortSignal | undefined,
  callback: () => void,
): () => void {
  if (!signal) {
    return () => undefined;
  }

  if (signal.aborted) {
    callback();
    return () => undefined;
  }

  signal.addEventListener("abort", callback, { once: true });
  return () => signal.removeEventListener("abort", callback);
}

export class GenerationBoundary {
  #generation = 0;
  #controller = new AbortController();

  get generation(): number {
    return this.#generation;
  }

  get signal(): AbortSignal {
    return this.#controller.signal;
  }

  advance(): { generation: number; signal: AbortSignal } {
    this.#controller.abort();
    this.#controller = new AbortController();
    this.#generation += 1;

    return {
      generation: this.#generation,
      signal: this.#controller.signal,
    };
  }

  cancel(): void {
    this.#controller.abort();
  }

  isCurrent(generation: number): boolean {
    return generation === this.#generation && !this.signal.aborted;
  }
}
